import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../../lib/utils';

const inputVariants = cva(
  [
    // The one field height, 36px, and the touch floor on the field ITSELF: a search box inside a dialog or an
    // inline edit in a card sits where no container band grants 44px, and a per-caller floor is how a toolbar
    // row drifted apart. Every derived field (register search, record picker, the range filter's custom
    // bounds) inherits both from here instead of restating them.
    // The text is the 14px interface step under a mouse and the 16px body step under a finger: iOS zooms the
    // page into any focused field set below 16px, and a zoomed page is one a reader then has to find their
    // way back out of. The chat composer takes the same `pointer-coarse:text-body`. Being a variant, it
    // overrules a caller's own size on touch; a caller drawn at 16px or more states its touch size as well
    // (`OneTimeCodeInput`).
    'h-9 w-full min-w-0 border border-input text-ui pointer-coarse:text-body text-foreground placeholder:text-muted-foreground pointer-coarse:min-h-[var(--touch-target)]',
    'transition-[border-color,background-color,box-shadow] selection:bg-primary selection:text-primary-foreground',
    'file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-ui file:text-foreground',
    'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
    'disabled:cursor-not-allowed disabled:opacity-40',
    'aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20',
  ],
  {
    variants: {
      variant: {
        default: 'rounded-md bg-control py-1 pl-3 pr-3',
        line: 'rounded-none border-x-0 border-t-0 bg-transparent px-0 py-1 focus-visible:ring-0',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Input({ className, type, variant, ...props }: React.ComponentProps<'input'> & VariantProps<typeof inputVariants>) {
  return (
    <input
      type={type}
      data-slot="input"
      data-variant={variant ?? 'default'}
      className={cn(inputVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Input };
